import isEqual from 'lodash/isEqual'
import * as React from 'react'
import { Query, QueryResult } from 'react-apollo'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { Bounds as MapBounds } from 'main/components/common/Map'
import Page from 'main/components/common/Page'
import ViewControl, { View } from 'main/components/common/SplitMapView/ViewControl'
import { useWhitelabel } from 'main/framework'
import { IMetatags } from 'main/framework/metatag-manifest'
import { IIpLocation } from 'main/gql/ipLocation'
import { regionSearchPath, schoolSearchLocationPath } from 'main/routes'
import { Hidden, ScreenClass, ScreenClassRender, Visible } from 'main/ui'
import {
  buildFiltersFromQuery,
  IFilters,
  IRankingFactorWeights,
  ISearchRegion,
} from 'main/util/search'
import { UnreachableCaseError } from 'main/util/ts'
import segment from 'util/analytics/segment'
import { FiltersBar, ListingSection, MapSection, RegionSEOCopy, RegionText } from '../../Content'
import {
  SEARCH_PAGE_QUERY,
  SearchPage as SearchPageData,
  SearchPage_result_listings,
  SearchPageVariables,
} from '../../gql/'
import { ISearchResults } from '../../types'
import { buildQueryVariables } from '../../util'
import {
  Container,
  SingleViewContainer,
  SplitViewListingSectionContainer,
  SplitViewMapSectionContainer,
} from './SearchPage.styles'

interface IProps extends RouteComponentProps {
  query: string
  metatags: IMetatags
  region: ISearchRegion | null
  ipLocation: IIpLocation | null
}

function SearchPage(props: IProps) {
  const {
    history: routerHistory,
    location: routerLocation,
    metatags,
    query,
    region,
    ipLocation,
  } = props

  const whitelabel = useWhitelabel()
  const allowableOptions = {
    selectedNetworkRatingsV2: whitelabel.networkRatingData().allowableFilterOptionValues,
  }
  const filtersFromRouterLocation = buildFiltersFromQuery(routerLocation.search, allowableOptions)
  // If we're using a search query then the initial radius should be 30mi
  // If using IP Geolocation the default is 10mi
  const initialRadius = query === '' ? 10 : 30

  const [filters, setFilters] = React.useState<IFilters>(filtersFromRouterLocation)
  const [radius, setRadius] = React.useState<number>(initialRadius)
  const [usingRadius, setUsingRadius] = React.useState<boolean>(true)
  const [rankingFactorWeights, setRankingFactorWeights] =
    React.useState<IRankingFactorWeights | null>(null)
  const [mapBounds, setMapBounds] = React.useState<MapBounds | null>(null)
  const [fetchingMore, setFetchingMore] = React.useState(false)

  const queryVariables = buildQueryVariables({
    filters,
    mapBounds,
    offset: 0,
    query,
    rankingFactorWeights,
    ipLocation,
    radius,
  })
  const [lastLoggedQueryVariables, setLastLoggedQueryVariables] =
    React.useState<SearchPageVariables | null>(null)

  // When URL query has changed, reset the filters from the new URL query.
  React.useEffect(() => {
    setFilters(filtersFromRouterLocation)
  }, [routerLocation.search])

  React.useEffect(() => {
    setUsingRadius(true)
  }, [radius])

  React.useEffect(() => {
    setUsingRadius(false)
  }, [mapBounds])

  // When query has changed, e.g. user has searched for a new location, unset the map bounds to
  // trigger searching the new query.
  React.useEffect(() => setMapBounds(null), [query])

  return (
    <Query<SearchPageData, SearchPageVariables>
      query={SEARCH_PAGE_QUERY}
      variables={queryVariables}
      onCompleted={(data: SearchPageData) => {
        if (!isEqual(lastLoggedQueryVariables, queryVariables) && data) {
          logSegmentData(data)
        }
      }}
    >
      {result => {
        return (
          <ScreenClassRender
            render={(screenClass: ScreenClass) => renderPage(result, screenClass)}
          />
        )
      }}
    </Query>
  )

  function renderPage(
    queryResult: QueryResult<SearchPageData, SearchPageVariables>,
    screenClass: ScreenClass,
  ) {
    const { data, loading, error, fetchMore } = queryResult
    const result = (data && data.result) || null

    return (
      <Page
        {...metatags}
        headerParams={{
          alwaysShowSearchForm: true,
          alwaysSticky: !['xs', 'sm'].includes(screenClass),
          currentSearch: { filters, query },
        }}
        headerBottomBar={
          <FiltersBar
            filters={filters}
            mapLocation={result ? result.location : null}
            onChange={handleChangeFilters}
          />
        }
        hideFooter
      >
        <Content
          result={result}
          region={region}
          fetchingMore={fetchingMore}
          loading={loading}
          error={!!error}
          mapBounds={mapBounds}
          filters={filters}
          rankingFactorWeights={rankingFactorWeights}
          searchRadius={radius}
          usingRadius={usingRadius}
          onChangeRankingWeights={setRankingFactorWeights}
          onChangeMapBounds={setMapBounds}
          onChangeSearchRadius={setRadius}
          onRequestNextPage={() => {
            if (!result) return
            if (fetchingMore) return

            setFetchingMore(true)
            fetchMore({
              updateQuery: (prev, { fetchMoreResult }) => {
                if (prev.result && fetchMoreResult && fetchMoreResult.result) {
                  return {
                    ...prev,
                    result: {
                      ...prev.result,
                      listings: [...prev.result.listings, ...fetchMoreResult.result.listings],
                    },
                  }
                }

                return prev
              },
              variables: {
                ...queryVariables,
                loadAllCoordinates: false,
                offset: result.listings.length,
              },
            }).then(() => setFetchingMore(false))
          }}
        />
      </Page>
    )
  }

  function handleChangeFilters(newFilters: IFilters) {
    const path = searchPath(query, newFilters, region)
    routerHistory.push(path)
  }

  function logSegmentData(data: SearchPageData) {
    const { result } = data
    if (!result) return

    segment.track('Search page inputs and results', {
      filters,
      mapBounds,
      numberOfResults: result.listings.length,
      queryString: query,
      top24SchoolResults: result.listings
        .slice(0, 24)
        .map(listing => createSchoolResultObject(listing)),
    })

    setLastLoggedQueryVariables(queryVariables)
  }

  function createSchoolResultObject(listing: SearchPage_result_listings) {
    return listing.externalSchoolId
      ? { id: listing.externalSchoolId, schoolType: 'external' }
      : { id: listing.schoolId, schoolType: 'internal' }
  }
}

function searchPath(query: string, filters: IFilters, region: ISearchRegion | null) {
  if (region) {
    return regionSearchPath(region, filters)
  } else {
    return schoolSearchLocationPath(query, filters)
  }
}

interface IContentProps extends ISearchResults {
  fetchingMore: boolean
  mapBounds: MapBounds | null
  filters: IFilters
  rankingFactorWeights: IRankingFactorWeights | null
  searchRadius: number
  usingRadius: boolean
  onChangeRankingWeights: (rankingWeights: IRankingFactorWeights) => void
  onChangeMapBounds: (bounds: MapBounds | null) => void
  onChangeSearchRadius: (radius: number) => void
  onRequestNextPage: () => void
}

function Content(props: IContentProps) {
  const {
    error,
    fetchingMore,
    loading,
    result,
    region,
    mapBounds,
    filters,
    rankingFactorWeights,
    searchRadius,
    usingRadius,
    onChangeRankingWeights,
    onChangeMapBounds,
    onChangeSearchRadius,
    onRequestNextPage,
  } = props
  const [currentView, setView] = React.useState<View>(View.Listing)

  return (
    <Container>
      <Visible lg xl>
        <SplitViewListingSectionContainer>
          {renderListingSection()}
        </SplitViewListingSectionContainer>
        <SplitViewMapSectionContainer>
          {renderMapSection({ mobile: false })}
        </SplitViewMapSectionContainer>
      </Visible>

      <Hidden lg xl>
        <SingleViewContainer>
          {renderCurrentView()}
          <ViewControl currentView={currentView} setView={setView} />
        </SingleViewContainer>
      </Hidden>
    </Container>
  )

  function renderMapSection({ mobile }: { mobile: boolean }) {
    return (
      <MapSection
        bounds={mapBounds}
        mobile={mobile}
        result={result}
        onChangeBounds={onChangeMapBounds}
      />
    )
  }

  function renderListingSection() {
    return (
      <>
        {maybeRenderRegionText()}
        <ListingSection
          error={error}
          fetchingMore={fetchingMore}
          loading={loading}
          result={result}
          filters={filters}
          searchRadius={searchRadius}
          rankingFactorWeights={rankingFactorWeights}
          usingRadius={usingRadius}
          onChangeRankingWeights={onChangeRankingWeights}
          onChangeSearchRadius={onChangeSearchRadius}
          onRequestNextPage={onRequestNextPage}
          regionSEOCopy={maybeRenderRegionSEOCopy()}
        />
      </>
    )
  }

  function renderCurrentView() {
    switch (currentView) {
      case View.Map:
        return renderMapSection({ mobile: true })
      case View.Listing:
        return renderListingSection()
      default:
        throw new UnreachableCaseError(currentView)
    }
  }

  function maybeRenderRegionText() {
    if (region) {
      return <RegionText city={region.city} state={region.state} />
    }
    return null
  }

  function maybeRenderRegionSEOCopy() {
    if (region) {
      return <RegionSEOCopy city={region.city} />
    }
    return null
  }
}

export default withRouter(SearchPage)
